import React from 'react';
import {
    Timeline,
    TimelineItem,
    TimelineSeparator,
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineOppositeContent,
} from '@mui/lab';
import { Typography, Paper, Box, Chip, Avatar } from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Inventory as InventoryIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

interface StatusChange {
    id: number;
    from_status: string | null;
    to_status: string;
    changed_by: string;
    changed_at: string;
    notes: string | null;
}

interface StatusHistoryTimelineProps {
    statusHistory: StatusChange[];
}

const STATUS_CONFIG: Record<string, { color: any; icon: React.ReactNode; label: string }> = {
    draft: {
        color: 'grey',
        icon: <InventoryIcon />,
        label: 'Draft',
    },
    confirmed: {
        color: 'info',
        icon: <CheckCircleIcon />,
        label: 'Confirmed',
    },
    completed: {
        color: 'success',
        icon: <CheckCircleIcon />,
        label: 'Completed',
    },
    cancelled: {
        color: 'error',
        icon: <CancelIcon />,
        label: 'Cancelled',
    },
};

const StatusHistoryTimeline: React.FC<StatusHistoryTimelineProps> = ({ statusHistory }) => {
    if (!statusHistory || statusHistory.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No status changes yet</Typography>
            </Box>
        );
    }

    return (
        <Timeline position="right">
            {statusHistory.map((change, index) => {
                const config = STATUS_CONFIG[change.to_status] || STATUS_CONFIG.draft;
                const isLast = index === statusHistory.length - 1;

                return (
                    <TimelineItem key={change.id}>
                        <TimelineOppositeContent color="text.secondary" sx={{ flex: 0.3 }}>
                            <Typography variant="caption" display="block">
                                {format(new Date(change.changed_at), 'MMM dd, yyyy')}
                            </Typography>
                            <Typography variant="caption" display="block">
                                {format(new Date(change.changed_at), 'hh:mm a')}
                            </Typography>
                        </TimelineOppositeContent>

                        <TimelineSeparator>
                            <TimelineDot color={config.color}>
                                {config.icon}
                            </TimelineDot>
                            {!isLast && <TimelineConnector />}
                        </TimelineSeparator>

                        <TimelineContent>
                            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Chip
                                        label={config.label}
                                        color={config.color}
                                        size="small"
                                    />
                                    {change.from_status && (
                                        <>
                                            <Typography variant="caption" color="text.secondary">
                                                from
                                            </Typography>
                                            <Chip
                                                label={STATUS_CONFIG[change.from_status]?.label || change.from_status}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </>
                                    )}
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                    <Avatar
                                        sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                                    >
                                        {change.changed_by.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Typography variant="body2" color="text.secondary">
                                        {change.changed_by}
                                    </Typography>
                                </Box>

                                {change.notes && (
                                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                        "{change.notes}"
                                    </Typography>
                                )}
                            </Paper>
                        </TimelineContent>
                    </TimelineItem>
                );
            })}
        </Timeline>
    );
};

export default StatusHistoryTimeline;
